import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, SquarePen, Send, ClipboardList, XCircle } from 'lucide-react';
import Layout from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import { damageNoteService } from '../../services/damageNoteService';
import {
  DamageNoteDTO,
  DamageNoteApprovalStatusDTO,
  DamageNoteApprovalDTO,
  DamageNoteStatus,
} from '../../types';
import './DamageNotePage.css';


import { getServerErrorMessage } from '../../utils/errorUtils';
const CATEGORY_CLASS: Record<string, string> = {
  Breakage:          'dn-cat-breakage',
  Expiry:            'dn-cat-expiry',
  Spoilage:          'dn-cat-spoilage',
  Leakage:           'dn-cat-leakage',
  OperationalDamage: 'dn-cat-operational',
};

const CATEGORY_LABEL: Record<string, string> = {
  Breakage:          'Breakage',
  Expiry:            'Expiry',
  Spoilage:          'Spoilage',
  Leakage:           'Leakage',
  OperationalDamage: 'Operational Damage',
};

// ─── Approval Level Row ───────────────────────────────────────────────────────

interface ApprovalLevelRowProps {
  approval:      DamageNoteApprovalDTO;
  currentUserId: number;
  onApprove:     (id: number) => void;
  onReject:      (id: number) => void;
}

const ApprovalLevelRow: React.FC<ApprovalLevelRowProps> = ({
  approval, currentUserId, onApprove, onReject,
}) => {
  const { t } = useTranslation();
  const isMine = approval.assignedToUserId === currentUserId && approval.status === 'Pending';

  const iconClass =
    approval.status === 'Approved' ? 'dn-level-icon--approved' :
    approval.status === 'Rejected' ? 'dn-level-icon--rejected' : 'dn-level-icon--pending';

  const icon =
    approval.status === 'Approved' ? '✓' :
    approval.status === 'Rejected' ? '✗' : approval.approvalLevel.toString();

  return (
    <div className={`dn-level-row ${isMine ? 'dn-level-row--mine' : ''}`}>
      <div className={`dn-level-icon ${iconClass}`}>{icon}</div>
      <div className="dn-level-info">
        <div className="dn-level-title">
          {t('damageNote.approvalLevel')} {approval.approvalLevel}
          {isMine && (
            <span className="dn-level-mine-badge" style={{ marginInlineStart: 8 }}>
              {t('damageNote.yourApproval')}
            </span>
          )}
        </div>
        <div className="dn-level-sub">
          {approval.status === 'Approved' && (
            <span>
              ✓ {t('damageNote.approvedBy')}: {approval.approvingUserName ?? approval.approvingUserId}
              {approval.approvedAt && ` · ${new Date(approval.approvedAt).toLocaleDateString()}`}
              {approval.approvalComments && ` · "${approval.approvalComments}"`}
            </span>
          )}
          {approval.status === 'Rejected' && (
            <span style={{ color: '#991b1b' }}>
              ✗ {t('damageNote.rejectedBy')}: {approval.approvingUserName}
              {approval.rejectionReason && ` · ${approval.rejectionReason}`}
            </span>
          )}
          {approval.status === 'Pending' && (
            <span>
              {t('damageNote.assignedTo')}: {approval.assignedToUserName ?? approval.assignedToUserId}
            </span>
          )}
        </div>
      </div>
      {isMine && (
        <div className="dn-level-actions">
          <button
            className="ims-action-btn ims-action-btn--approve"
            onClick={() => onApprove(approval.id)}
          >
            {t('common.approve')}
          </button>
          <button
            className="ims-action-btn ims-action-btn--reject"
            onClick={() => onReject(approval.id)}
          >
            {t('common.reject')}
          </button>
        </div>
      )}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const DamageNoteDetailPage: React.FC = () => {
  const { id }    = useParams<{ id: string }>();
  const navigate  = useNavigate();
  const { t }     = useTranslation();
  const { user }  = useAuth();

  const STATUS_LABEL: Record<DamageNoteStatus, string> = {
    Draft:           t('statuses.draft'),
    PendingApproval: t('statuses.pendingApproval'),
    Approved:        t('statuses.approved'),
    Rejected:        t('statuses.rejected'),
    Posted:          t('statuses.posted'),
    Cancelled:       t('statuses.cancelled'),
  };

  const STATUS_CLASS: Record<DamageNoteStatus, string> = {
    Draft:           'ims-badge dn-badge-draft',
    PendingApproval: 'ims-badge dn-badge-pending',
    Approved:        'ims-badge dn-badge-approved',
    Rejected:        'ims-badge dn-badge-rejected',
    Posted:          'ims-badge dn-badge-posted',
    Cancelled:       'ims-badge dn-badge-cancelled',
  };

  const [damageNote, setDamageNote]         = useState<DamageNoteDTO | null>(null);
  const [approvalStatus, setApprovalStatus] = useState<DamageNoteApprovalStatusDTO | null>(null);
  const [loading, setLoading]               = useState(true);
  const [actionLoading, setActionLoading]   = useState(false);
  const [error, setError]                   = useState<string | null>(null);

  // Modal states
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal]   = useState(false);
  const [pendingApprovalId, setPendingApprovalId] = useState<number | null>(null);
  const [approveComments, setApproveComments]   = useState('');
  const [rejectReason, setRejectReason]         = useState('');

  const userId = user?.id ?? 0;

  const fetchAll = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const [dnRes, aRes] = await Promise.all([
        damageNoteService.getById(id),
        damageNoteService.getApprovalStatus(id),
      ]);
      const dn = dnRes.data?.data;
      if (dn) setDamageNote(dn);
      const a = aRes.data?.data;
      if (a) setApprovalStatus(a);
    } catch {
      setError(t('damageNote.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const myPendingApproval = approvalStatus?.approvals.find(
    (a) => a.assignedToUserId === userId && a.status === 'Pending'
  ) ?? null;

  // ── Actions ───────────────────────────────────────────────────────────────

  const handleSubmit = async () => {
    if (!id) return;
    setActionLoading(true);
    try {
      await damageNoteService.submit(id, userId);
      await fetchAll();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: { message?: string } } } };
      setError(getServerErrorMessage(err) ?? t('damageNote.failedToSubmit'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleApproveOpen = (approvalId: number) => {
    setPendingApprovalId(approvalId);
    setApproveComments('');
    setShowApproveModal(true);
  };

  const handleRejectOpen = (approvalId: number) => {
    setPendingApprovalId(approvalId);
    setRejectReason('');
    setShowRejectModal(true);
  };

  const handleApproveConfirm = async () => {
    if (!pendingApprovalId) return;
    setActionLoading(true);
    try {
      await damageNoteService.approve(pendingApprovalId, userId, approveComments || undefined);
      setShowApproveModal(false);
      await fetchAll();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: { message?: string } } } };
      setError(getServerErrorMessage(err) ?? t('common.error'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectConfirm = async () => {
    if (!pendingApprovalId || !rejectReason.trim()) return;
    setActionLoading(true);
    try {
      await damageNoteService.reject(pendingApprovalId, userId, rejectReason);
      setShowRejectModal(false);
      await fetchAll();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: { message?: string } } } };
      setError(getServerErrorMessage(err) ?? t('common.error'));
    } finally {
      setActionLoading(false);
    }
  };

  const handlePost = async () => {
    if (!id) return;
    const confirmed = window.confirm(
      t('damageNote.confirmPost')
    );
    if (!confirmed) return;
    setActionLoading(true);
    try {
      await damageNoteService.post(id, userId);
      await fetchAll();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: { message?: string } } } };
      setError(getServerErrorMessage(err) ?? t('damageNote.failedToPost'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!id || !window.confirm(t('damageNote.confirmCancel'))) return;
    setActionLoading(true);
    try {
      const reason = prompt(t('damageNote.cancellationReasonPrompt')) ?? '';
      await damageNoteService.cancel(id, userId, reason);
      await fetchAll();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: { message?: string } } } };
      setError(getServerErrorMessage(err) ?? t('common.error'));
    } finally {
      setActionLoading(false);
    }
  };

  // ── Loading / not found guards ────────────────────────────────────────────

  if (loading) {
    return (
      <Layout>
        <div className="ims-page dn-page">
          <div className="ims-loading">{t('common.loading')}</div>
        </div>
      </Layout>
    );
  }

  if (error && !damageNote) {
    return (
      <Layout>
        <div className="ims-page dn-page">
          <div className="ims-alert ims-alert-error">{error}</div>
        </div>
      </Layout>
    );
  }

  if (!damageNote) {
    return (
      <Layout>
        <div className="ims-page dn-page">
          <div className="ims-alert ims-alert-error">{t('damageNote.notFound')}</div>
        </div>
      </Layout>
    );
  }

  // ── Computed flags ────────────────────────────────────────────────────────

  const status    = damageNote.status as DamageNoteStatus;
  const canEdit   = status === 'Draft';
  const canSubmit = status === 'Draft' || status === 'Rejected';
  const canPost   = status === 'Approved';
  const canCancel = ['Draft', 'PendingApproval', 'Approved', 'Rejected'].includes(status);

  const steps = [
    { label: t('damageNote.stepDraft'),    done: true },
    { label: t('damageNote.stepApproval'), done: ['Approved', 'Posted'].includes(status) },
    { label: t('damageNote.stepPosted'),   done: status === 'Posted' },
  ];

  const totalValue = damageNote.totalEstimatedValue;

  return (
    <Layout>
      <div className="ims-detail-page dn-page">

        {/* Hero */}
        <div className="ims-hero dn-hero">
          <div className="ims-hero-nav">
            <button className="ims-hero-back" onClick={() => navigate('/damage-notes')}>
              <ArrowLeft size={15} strokeWidth={2} /> {t('common.back')}
            </button>
          </div>
          <div className="ims-hero-content">
            <div>
              <h1 className="ims-hero-number">{damageNote.damageNoteNumber}</h1>
              <p className="ims-hero-supplier">{damageNote.warehouseName}</p>
              <div className="ims-hero-badges">
                <span className={STATUS_CLASS[status] || 'ims-badge'}>
                  {STATUS_LABEL[status] ?? status}
                </span>
              </div>
            </div>
            <div className="ims-hero-meta">
              <span className="ims-hero-date">
                {new Date(damageNote.damageDate).toLocaleDateString()}
              </span>
            </div>
          </div>
        </div>

        {/* Steps */}
        <div className="ims-steps">
          {steps.map((step, i) => (
            <React.Fragment key={step.label}>
              <div className={`ims-step ${step.done ? 'ims-step--done' : ''}`}>
                <div className="ims-step-dot">{step.done ? '✓' : i + 1}</div>
                {step.label}
              </div>
              {i < steps.length - 1 && <span className="ims-step-arrow">›</span>}
            </React.Fragment>
          ))}
        </div>

        {error && (
          <div className="ims-alert ims-alert-error" style={{ margin: '0 20px 8px' }}>
            {error}
          </div>
        )}

        {/* Body */}
        <div className="ims-detail-body">

          {/* ── Main column ── */}
          <div className="ims-detail-main">

            {/* Damage Note info card */}
            <div className="ims-card">
              <div className="ims-card-header">
                <span className="ims-card-title">{t('damageNote.damageNoteDetails')}</span>
              </div>
              <div className="ims-info-grid">
                <div className="ims-info-field">
                  <span className="ims-info-label">{t('damageNote.warehouse')}</span>
                  <span className="ims-info-value">{damageNote.warehouseName}</span>
                </div>
                <div className="ims-info-field">
                  <span className="ims-info-label">{t('damageNote.damageDate')}</span>
                  <span className="ims-info-value">
                    {new Date(damageNote.damageDate).toLocaleDateString()}
                  </span>
                </div>
                <div className="ims-info-field">
                  <span className="ims-info-label">{t('damageNote.reportedBy')}</span>
                  <span className="ims-info-value">{damageNote.reportedByName}</span>
                </div>
                {damageNote.postedByName && (
                  <div className="ims-info-field">
                    <span className="ims-info-label">{t('damageNote.postedBy')}</span>
                    <span className="ims-info-value">
                      {damageNote.postedByName}
                      {damageNote.postedAt && ` · ${new Date(damageNote.postedAt).toLocaleDateString()}`}
                    </span>
                  </div>
                )}
                {damageNote.notes && (
                  <div className="ims-info-field" style={{ gridColumn: '1 / -1' }}>
                    <span className="ims-info-label">{t('common.notes')}</span>
                    <span className="ims-info-value">{damageNote.notes}</span>
                  </div>
                )}
                {damageNote.rejectionNotes && (
                  <div className="ims-info-field" style={{ gridColumn: '1 / -1' }}>
                    <span className="ims-info-label">{t('damageNote.rejectionNotes')}</span>
                    <span className="ims-info-value" style={{ color: '#991b1b' }}>
                      {damageNote.rejectionNotes}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Items table */}
            <div className="ims-card" style={{ marginTop: 16 }}>
              <div className="ims-card-header">
                <span className="ims-card-title">{t('damageNote.damagedItems')}</span>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table className="ims-table" style={{ width: '100%' }}>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>{t('damageNote.product')}</th>
                      <th>{t('damageNote.unit')}</th>
                      <th style={{ textAlign: 'right' }}>{t('damageNote.quantity')}</th>
                      <th>{t('damageNote.damageCategory')}</th>
                      <th style={{ textAlign: 'right' }}>{t('damageNote.estimatedValue')}</th>
                      <th>{t('damageNote.reasonNotes')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {damageNote.items.map((item, idx) => (
                      <tr key={item.id}>
                        <td>{idx + 1}</td>
                        <td>
                          <div style={{ fontWeight: 600 }}>{item.productName}</div>
                          {item.productCode && (
                            <div style={{ fontSize: '0.75rem', color: 'var(--ims-text-muted)' }}>
                              {item.productCode}
                            </div>
                          )}
                        </td>
                        <td>{item.unitName}</td>
                        <td style={{ textAlign: 'right' }}>{item.quantity}</td>
                        <td>
                          <span
                            className={`ims-badge ${CATEGORY_CLASS[item.damageCategory] ?? ''}`}
                          >
                            {CATEGORY_LABEL[item.damageCategory] ?? item.damageCategory}
                          </span>
                        </td>
                        <td style={{ textAlign: 'right' }}>{item.estimatedValue.toFixed(2)}</td>
                        <td style={{ fontSize: '0.8rem', color: 'var(--ims-text-sub)' }}>
                          {item.reasonNotes ?? '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Total estimated value */}
              <div className="dn-total-loss-box">
                <span className="dn-loss-label">{t('damageNote.totalEstimatedValue')}</span>
                <span className="dn-loss-value">{totalValue.toFixed(2)}</span>
              </div>
            </div>

            {/* Approval levels */}
            {approvalStatus && approvalStatus.approvals.length > 0 && (
              <div className="ims-card" style={{ marginTop: 16 }}>
                <div className="ims-card-header">
                  <span className="ims-card-title">{t('damageNote.approvalLevels')}</span>
                  <span style={{ fontSize: '0.8rem', color: 'var(--ims-text-muted)' }}>
                    {approvalStatus.currentApprovalLevel}/{approvalStatus.totalApprovalLevels}{' '}
                    {t('damageNote.levelsCompleted')}
                  </span>
                </div>
                <div className="ims-card-body">
                  {myPendingApproval && (
                    <div className="dn-pending-banner">
                      ⏳ {t('damageNote.pendingYourApproval')}
                    </div>
                  )}
                  <div className="dn-levels-list">
                    {approvalStatus.approvals.map((a) => (
                      <ApprovalLevelRow
                        key={a.id}
                        approval={a}
                        currentUserId={userId}
                        onApprove={handleApproveOpen}
                        onReject={handleRejectOpen}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ── Sidebar ── */}
          <div className="ims-detail-sidebar">

            {/* Actions card */}
            <div className="ims-card">
              <div className="ims-card-header">
                <span className="ims-card-title">{t('common.actions')}</span>
              </div>
              <div className="ims-card-body">
                <div className="ims-actions-list">
                  {canEdit && (
                    <button
                      className="ims-action-btn ims-action-btn--edit"
                      onClick={() => navigate(`/damage-notes/${id}/edit`)}
                    >
                      <SquarePen size={15} strokeWidth={2} /> {t('common.edit')}
                    </button>
                  )}
                  {canSubmit && (
                    <button
                      className="ims-action-btn ims-action-btn--approve"
                      onClick={handleSubmit}
                      disabled={actionLoading}
                    >
                      <Send size={15} strokeWidth={2} /> {t('damageNote.submitForApproval')}
                    </button>
                  )}
                  {status === 'PendingApproval' && !myPendingApproval && (
                    <div
                      className="ims-action-btn"
                      style={{ background: '#f3f4f6', color: '#4b5563', cursor: 'default' }}
                    >
                      ⏳ {t('damageNote.awaitingApproval')}
                    </div>
                  )}
                  {canPost && (
                    <button
                      className="ims-action-btn ims-action-btn--post"
                      onClick={handlePost}
                      disabled={actionLoading}
                    >
                      <ClipboardList size={15} strokeWidth={2} /> {t('damageNote.postDamageNote')}
                    </button>
                  )}
                  {canCancel && (
                    <button
                      className="ims-action-btn ims-action-btn--reject"
                      onClick={handleCancel}
                      disabled={actionLoading}
                    >
                      <XCircle size={15} strokeWidth={2} /> {t('damageNote.cancel')}
                    </button>
                  )}
                  <button
                    className="ims-action-btn ims-action-btn--edit"
                    onClick={() => navigate('/damage-notes')}
                  >
                    <ArrowLeft size={15} strokeWidth={2} /> {t('common.back')}
                  </button>
                </div>
              </div>
            </div>

            {/* Summary card */}
            <div className="ims-card">
              <div className="ims-card-header">
                <span className="ims-card-title">{t('damageNote.summary')}</span>
              </div>
              <div className="ims-card-body">
                <div className="ims-info-grid" style={{ gridTemplateColumns: '1fr' }}>
                  <div className="ims-info-field">
                    <span className="ims-info-label">{t('damageNote.totalItems')}</span>
                    <span className="ims-info-value" style={{ fontWeight: 700 }}>
                      {damageNote.items.length}
                    </span>
                  </div>
                  <div className="ims-info-field">
                    <span className="ims-info-label">{t('damageNote.totalEstimatedLoss')}</span>
                    <span
                      className="ims-info-value"
                      style={{ fontWeight: 700, color: '#991b1b', fontSize: '1.05rem' }}
                    >
                      {totalValue.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* Approve Modal */}
      {showApproveModal && (
        <div className="ims-modal-overlay" onClick={() => setShowApproveModal(false)}>
          <div className="ims-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ims-modal-header">
              <h3>{t('damageNote.approveTitle')}</h3>
              <button className="ims-modal-close" onClick={() => setShowApproveModal(false)}>×</button>
            </div>
            <div className="ims-modal-body">
              <div className="ims-form-group">
                <label className="ims-form-label">
                  {t('damageNote.approvalComments')} ({t('common.optional')})
                </label>
                <textarea
                  className="ims-form-control"
                  rows={3}
                  value={approveComments}
                  onChange={(e) => setApproveComments(e.target.value)}
                  placeholder={t('damageNote.approvalCommentsPlaceholder')}
                />
              </div>
            </div>
            <div className="ims-modal-footer">
              <button
                className="btn-cancel"
                onClick={() => setShowApproveModal(false)}
              >
                {t('common.cancel')}
              </button>
              <button
                className="ims-btn ims-btn-success"
                onClick={handleApproveConfirm}
                disabled={actionLoading}
              >
                {actionLoading ? '…' : t('common.approve')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="ims-modal-overlay" onClick={() => setShowRejectModal(false)}>
          <div className="ims-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ims-modal-header">
              <h3>{t('damageNote.rejectTitle')}</h3>
              <button className="ims-modal-close" onClick={() => setShowRejectModal(false)}>×</button>
            </div>
            <div className="ims-modal-body">
              <div className="ims-form-group">
                <label className="ims-form-label">
                  {t('damageNote.rejectionReason')} <span className="required">*</span>
                </label>
                <textarea
                  className="ims-form-control"
                  rows={3}
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder={t('damageNote.rejectionReasonPlaceholder')}
                />
              </div>
            </div>
            <div className="ims-modal-footer">
              <button
                className="btn-cancel"
                onClick={() => setShowRejectModal(false)}
              >
                {t('common.cancel')}
              </button>
              <button
                className="btn-delete"
                onClick={handleRejectConfirm}
                disabled={!rejectReason.trim() || actionLoading}
              >
                {actionLoading ? '…' : t('common.reject')}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default DamageNoteDetailPage;
